import React, { useEffect, useState, useCallback } from 'react';
import { TrendingUp, TrendingDown, Scale, Tag } from 'lucide-react';
import api from '../../api/client';

const fmt = (n) => `€ ${Math.abs(n || 0).toFixed(2)}`;
const oggiISO = () => new Date().toISOString().slice(0, 10);
const primoGennaioISO = () => `${new Date().getFullYear()}-01-01`;

// La stagione sportiva va da settembre ad agosto (non l'anno solare) — se siamo tra
// settembre e dicembre la stagione in corso è iniziata quest'anno, altrimenti l'anno scorso.
const stagioneCorrente = () => {
  const oggi = new Date();
  const annoInizio = oggi.getMonth() >= 8 ? oggi.getFullYear() : oggi.getFullYear() - 1;
  return { da: `${annoInizio}-09-01`, a: `${annoInizio + 1}-08-31` };
};

export default function ResocontoSpese() {
  const [dati, setDati] = useState(null);
  const [da, setDa] = useState(primoGennaioISO());
  const [a, setA] = useState(oggiISO());
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (da) params.set('da', da);
      if (a) params.set('a', a);
      const r = await api.get(`/banca/bilancio-periodo?${params.toString()}`);
      setDati(r.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [da, a]);

  useEffect(() => { load(); }, [load]);

  const preset = (tipo) => {
    if (tipo === 'mese') { const d = new Date(); setDa(new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10)); setA(oggiISO()); }
    else if (tipo === 'anno') { setDa(primoGennaioISO()); setA(oggiISO()); }
    else if (tipo === 'stagione') { const s = stagioneCorrente(); setDa(s.da); setA(s.a); }
    else if (tipo === 'tutto') { setDa(''); setA(''); }
  };

  const usciteOrdinate = (dati?.perCategoria || []).filter(c => c.totale < 0);
  const entrateOrdinate = (dati?.perCategoria || []).filter(c => c.totale > 0);
  const maxUscita = Math.max(1, ...usciteOrdinate.map(c => Math.abs(c.totale)));

  return (
    <div>
      <div style={s.header}>
        <div>
          <h1 style={s.title}>RESOCONTO SPESE</h1>
          <p style={s.sub}>Entrate e uscite per categoria nel periodo scelto — solo movimenti bancari categorizzati (i movimenti di cassa contanti hanno causali, non categorie, e non entrano in questo resoconto).</p>
        </div>
      </div>

      <div style={s.filterBar}>
        <button style={s.presetBtn} onClick={() => preset('mese')}>QUESTO MESE</button>
        <button style={s.presetBtn} onClick={() => preset('anno')}>QUEST'ANNO</button>
        <button style={{ ...s.presetBtn, borderColor: '#F4600C50', color: '#F4600C' }} onClick={() => preset('stagione')}>STAGIONE SPORTIVA</button>
        <button style={s.presetBtn} onClick={() => preset('tutto')}>SEMPRE</button>
        <div style={{ width: '1px', background: 'var(--border2)', margin: '0 6px' }} />
        <label style={s.label}>DAL</label>
        <input type="date" style={s.dateInput} value={da} onChange={e => setDa(e.target.value)} />
        <label style={s.label}>AL</label>
        <input type="date" style={s.dateInput} value={a} onChange={e => setA(e.target.value)} />
      </div>

      {loading && <div style={{ color: '#666', padding: '20px' }}>Caricamento...</div>}

      {dati && !loading && (
        <>
          <div style={s.totaliRow}>
            <div style={s.totaleCard}>
              <TrendingUp size={16} color="#22C55E" />
              <div><div style={s.totaleLabel}>ENTRATE</div><div style={{ ...s.totaleValore, color: '#22C55E' }}>{fmt(dati.totEntrate)}</div></div>
            </div>
            <div style={s.totaleCard}>
              <TrendingDown size={16} color="#EF4444" />
              <div><div style={s.totaleLabel}>USCITE</div><div style={{ ...s.totaleValore, color: '#EF4444' }}>{fmt(dati.totUscite)}</div></div>
            </div>
            <div style={s.totaleCard}>
              <Scale size={16} color={dati.saldo >= 0 ? '#22C55E' : '#EF4444'} />
              <div><div style={s.totaleLabel}>SALDO</div><div style={{ ...s.totaleValore, color: dati.saldo >= 0 ? '#22C55E' : '#EF4444' }}>{dati.saldo >= 0 ? '+' : '-'}{fmt(dati.saldo)}</div></div>
            </div>
          </div>

          <div style={s.formTitle}>USCITE PER CATEGORIA</div>
          {usciteOrdinate.length === 0 ? (
            <div style={{ color: 'var(--text-dim)', fontSize: 'var(--font-size-sm)', padding: '20px 0' }}>Nessuna uscita categorizzata nel periodo.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '28px' }}>
              {usciteOrdinate.map(c => (
                <div key={c.nome + c.centro} style={s.rigaCat}>
                  <div style={{ ...s.dot, background: c.colore }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 'var(--font-size-sm)' }}>{c.nome}</div>
                    <div style={{ fontSize: 'var(--font-size-xs)', color: '#666' }}>{c.centro} · {c.n} movimenti</div>
                    <div style={s.barraTrack}><div style={{ ...s.barraFill, width: `${(Math.abs(c.totale) / maxUscita) * 100}%`, background: c.colore }} /></div>
                  </div>
                  <div style={{ fontWeight: 800, fontFamily: "'Barlow Condensed'", fontSize: 'var(--font-size-lg)', color: '#EF4444', minWidth: '90px', textAlign: 'right' }}>{fmt(c.totale)}</div>
                </div>
              ))}
            </div>
          )}

          <div style={s.formTitle}>ENTRATE PER CATEGORIA</div>
          {entrateOrdinate.length === 0 ? (
            <div style={{ color: 'var(--text-dim)', fontSize: 'var(--font-size-sm)', padding: '20px 0' }}>Nessuna entrata categorizzata nel periodo.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {entrateOrdinate.map(c => (
                <div key={c.nome + c.centro} style={s.rigaCat}>
                  <div style={{ ...s.dot, background: c.colore }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 'var(--font-size-sm)' }}>{c.nome}</div>
                    <div style={{ fontSize: 'var(--font-size-xs)', color: '#666' }}>{c.centro} · {c.n} movimenti</div>
                  </div>
                  <div style={{ fontWeight: 800, fontFamily: "'Barlow Condensed'", fontSize: 'var(--font-size-lg)', color: '#22C55E', minWidth: '90px', textAlign: 'right' }}>{fmt(c.totale)}</div>
                </div>
              ))}
            </div>
          )}

          {dati.nMovimenti === 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#666', padding: '20px', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '10px', marginTop: '16px' }}>
              <Tag size={16} color="#555" />
              Nessun movimento bancario trovato nel periodo scelto — o non ce ne sono ancora, o va rifatto l'import dell'estratto conto da Movimenti bancari.
            </div>
          )}
        </>
      )}
    </div>
  );
}

const s = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' },
  title: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: 'var(--font-size-title)', fontWeight: '800', letterSpacing: '2px' },
  sub: { color: 'var(--text-dim)', fontSize: 'var(--font-size-sm)', marginTop: '4px', maxWidth: '640px' },
  filterBar: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' },
  presetBtn: { background: 'var(--bg-input)', border: '1px solid var(--border2)', borderRadius: '6px', padding: '7px 12px', color: 'var(--text-muted)', fontSize: 'var(--font-size-xs)', fontWeight: '700', cursor: 'pointer' },
  dateInput: { background: 'var(--bg-input)', border: '1px solid var(--border2)', borderRadius: '6px', padding: '6px 10px', color: 'var(--text)', fontSize: 'var(--font-size-xs)', outline: 'none' },
  label: { fontSize: 'var(--font-size-xs)', letterSpacing: '1px', fontWeight: '700', color: 'var(--text-dim)' },
  totaliRow: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '24px' },
  totaleCard: { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '10px', padding: '14px 18px', display: 'flex', alignItems: 'center', gap: '12px' },
  totaleLabel: { fontSize: 'var(--font-size-xs)', letterSpacing: '1px', color: 'var(--text-dim)', fontWeight: '700' },
  totaleValore: { fontSize: 'var(--font-size-lg)', fontWeight: '800' },
  formTitle: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: 'var(--font-size-sm)', fontWeight: '700', letterSpacing: '2px', marginBottom: '12px', color: 'var(--text-dim)' },
  rigaCat: { display: 'flex', alignItems: 'center', gap: '12px', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '10px', padding: '10px 16px' },
  dot: { width: '10px', height: '10px', borderRadius: '50%', flexShrink: 0 },
  barraTrack: { height: '4px', background: 'var(--bg-input)', borderRadius: '2px', marginTop: '6px', overflow: 'hidden' },
  barraFill: { height: '100%', borderRadius: '2px' },
};
